
import { computed, defineComponent, onBeforeMount, onMounted, reactive, Ref, ref } from 'vue'
import { useRoute } from 'vue-router'

import OkSwitch from '@/modules/OktellModule/components/OkSwitch/index.vue'
import OkSelect from '@/modules/OktellModule/components/OkSelect/index.vue'
import OkButton from '@/modules/OktellModule/components/OkButton/index.vue'
import OkConfirm from '@/modules/OktellModule/components/OkConfirm/index.vue'
import OkOrderDepartureDate from '@/modules/OktellModule/components/OkOrderDepartureDate/index.vue'
import OkInput from '@/modules/OktellModule/components/OkInput/index.vue'
import OkCheckbox from '@/modules/OktellModule/components/OkCheckbox/index.vue'

import { oktell } from '@/core/realisations/oktell.realisation'

import {
    CreateCallBackPayload,
    OktellRandomCallPayload,
    OktellReason,
} from '@/core/types/oktell.types'

import useNotifications from '@/common/composable/useNotifications'

import useVuelidate from '@vuelidate/core'
import { requiredIf } from '@vuelidate/validators'
import { TIME_MASK, OKTELL_PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'

const TYPES = [
    { id: 3, name: 'Перезвон' },
    { id: 1, name: 'Успешный' },
    { id: 2, name: 'Неуспешный' },
]

export default defineComponent({
    components: {
        OkSwitch,
        OkSelect,
        OkButton,
        OkConfirm,
        OkOrderDepartureDate,
        OkInput,
        OkCheckbox,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()

        const phone = route.query.phone as string | undefined
        const callDirection = route.query.callDirection as string | undefined
        const callcenterPhone = route.query.callcenterPhone as string | undefined
        const idChain = route.query.idChain as string | undefined
        const idInList = route.query.idInList as string | undefined
        const project = route.query.project as string | undefined
        const callTaskUuid = route.query.callTaskUuid as string | undefined

        const type = ref(TYPES[0].id)
        const is_loading = ref(false)
        const is_confirming = ref(false)
        const dateMin = ref(null) as Ref<string | null>

        const state = reactive({
            reasonId: null as number | null,
            //
            date: null as string | null,
            time: null as string | null,
            clientName: null as string | null,
            phone: null as string | null,
            recallForWA: false,
        })

        const isCallBack = computed(() => type.value === 3)

        const state_form$ = useVuelidate(
            {
                reasonId: { required: requiredIf(() => !isCallBack.value) },
                //
                date: { required: requiredIf(() => isCallBack.value) },
                time: { required: requiredIf(() => isCallBack.value) },
            },
            state
        )

        const reasons_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as OktellReason[],
            filtered_list: [] as OktellReason[],
        })

        const refilterReasons = (type: number) => {
            state.reasonId = null
            reasons_state.filtered_list = reasons_state.list.filter(
                (reason) => reason.type === type && ![8, 9].includes(reason.id)
            )
        }

        const loadReasons = async () => {
            try {
                reasons_state.is_loading = true

                const response = await oktell.getReasons()
                reasons_state.list = response.data

                refilterReasons(type.value)

                reasons_state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка причин произошла ошибка',
                })
                reasons_state.is_failed = true
            } finally {
                reasons_state.is_loading = false
            }
        }

        const getCallPayload = () => {
            const payload: OktellRandomCallPayload = {
                contactPhone: phone as string,
                direction: callDirection as string,
                result: '',
                chainId: idChain as string,
                type: isCallBack.value ? 2 : type.value,
                callcenterPhone: callcenterPhone as string,
                project,
                callTaskUuid,
            }
            payload.reasonId = isCallBack.value ? 9 : (state.reasonId as number)

            return payload
        }

        const getCallBackPayload = () => {
            const payloadCallback: CreateCallBackPayload = {
                date: getDate() as string,
                time: state.time as string,
                clientName: state.clientName,
                phone: state.phone,
                chainId: idChain as string,
                oktellId: idInList ? Number(idInList) : null,
                recallForWA: state.recallForWA,
            }

            return payloadCallback
        }

        const getDate = () => {
            if (!state.date) return
            const date = state.date.split('.').reverse().join('-')
            return date
        }

        const onConfirm = async () => {
            is_confirming.value = false

            try {
                is_loading.value = true

                await oktell.randomCall(getCallPayload())
                notifications.notify({ title: 'Звонок успешно создан', type: 'success' })

                if (isCallBack.value) {
                    await oktell.createCallBack(getCallBackPayload())
                    notifications.notify({ title: 'Перезвон успешно создан', type: 'success' })
                }

                await oktell.dialogCardNext()

                state.reasonId = null
                state_form$.value.$reset()

                notifications.notify({ title: 'Запрос отправлен', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При отправке запроса произошла ошибка' })
            } finally {
                is_loading.value = false
            }
        }

        const onCancel = () => {
            is_confirming.value = false
        }

        const onSubmit = () => {
            if (is_loading.value) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            is_confirming.value = true
        }

        const initializeDateMin = () => {
            const today = new Date()
            dateMin.value = today.toISOString().substring(0, 10)
        }

        onBeforeMount(loadReasons)
        onMounted(initializeDateMin)

        const triggerRecallForWA = () => {
            if (is_confirming.value) return
            state.recallForWA = !state.recallForWA
        }

        return {
            type,
            TYPES,
            is_loading,
            is_confirming,
            state,
            state_form$,
            reasons_state,
            loadReasons,
            refilterReasons,
            onSubmit,
            onConfirm,
            onCancel,
            dateMin,
            isCallBack,
            TIME_MASK,
            OKTELL_PHONE_NUMBER_MASK,
            triggerRecallForWA,
        }
    },
})
